import { Component, Inject, OnDestroy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { Tools } from "@comunicare/comunicare-helper";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { first } from "rxjs/operators";
import Validation from "src/app/components/forms/init-password/password-match.validation";
import { FormPage } from "src/app/pages/formPage";
import { AuthApiService } from "src/app/providers/api/auth-api.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";

/** How long a captcha stays valid before it is automatically refreshed. */
const CAPTCHA_DURATION_S = 30;

/** Maximum number of automatic refreshes before the modal is locked. */
const MAX_CAPTCHA_AUTO_REFRESH = 5;

export interface ForgotPasswordModalData {
  /** Reset code pre-filled from the `?resetCode=` URL parameter. */
  resetCode?: string;
}

/**
 * Two-step password reset modal.
 *
 * **Step 1 – Captcha**
 * The user enters their login and fetches a captcha image via
 * `GET /dashboard/captcha`. After solving it they submit via `POST /dashboard/captcha`.
 * The captcha auto-refreshes every 30 s (up to 5 times); after that the modal is locked.
 * On success the backend sends a reset link by e-mail.
 *
 * **Step 2 – New password**
 * Opened directly when a `resetCode` is provided in the dialog data (e-mail link flow).
 * The user supplies their login, the reset code and their new password.
 * `POST /dashboard/newPractitionerPassword` is called; on success the access token is
 * stored and the modal closes with `{ success: true }` so the login page can navigate.
 */
@Component({
  selector: "app-forgot-password-modal",
  templateUrl: "./forgot-password-modal.component.html",
  styleUrls: ["./forgot-password-modal.component.scss"],
})
export class ForgotPasswordModalComponent extends FormPage implements OnDestroy {
  /** Which step is currently displayed. */
  public step: "captcha" | "reset" = "captcha";

  /** Form for step 1 (login + captcha code). */
  public captchaForm: UntypedFormGroup;

  /** Form for step 2 (login + resetCode + new passwords). */
  public resetForm: UntypedFormGroup;

  /** Trusted URL of the SVG captcha image returned by the backend. */
  public captchaImageUrl: SafeUrl;

  public captchaLoaded = false;

  /** True while fetching the captcha or submitting the captcha form. */
  public captchaLoading = false;

  /** Error message for the captcha step (API key or translated string). */
  public captchaError: string;

  /** True after a successful captcha submission (e-mail sent by the backend). */
  public captchaSent = false;

  /** True when the maximum number of refreshes has been reached. */
  public captchaBlocked = false;

  /** Seconds remaining before the next automatic captcha refresh. */
  public countdown = CAPTCHA_DURATION_S;

  /** Error message for the reset step. */
  public resetError: string;

  /** True while `POST /dashboard/newPractitionerPassword` is in flight. */
  public resetLoading = false;

  private onDestroy$ = new Subject<void>();

  /** Object URL created from the captcha SVG blob; revoked on every reload to avoid memory leaks. */
  private captchaObjectUrl: string;

  /** `setInterval` handle for the countdown; null when not running. */
  private captchaIntervalId: number;

  /** Number of automatic refreshes triggered so far (timer expiry or wrong code). */
  private captchaAutoRefreshCount = 0;

  constructor(
    private fb: UntypedFormBuilder,
    private authApi: AuthApiService,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private sessionService: SessionService,
    private userService: UserService,
    private dialogRef: MatDialogRef<ForgotPasswordModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ForgotPasswordModalData
  ) {
    super();
    this.captchaForm = this.fb.group({
      login: ["", [Validators.required]],
      captchaCode: ["", [Validators.required]],
    });
    this.resetForm = this.fb.group(
      {
        login: ["", [Validators.required]],
        resetCode: ["", [Validators.required]],
        newPasswordA: ["", [Validators.required, Validators.pattern(Tools.PWD_REGEX)]],
        newPasswordB: ["", [Validators.required]],
      },
      { validators: [Validation.match("newPasswordA", "newPasswordB")] }
    );

    // Skip step 1 entirely when a reset code is already available (e-mail link flow).
    if (data?.resetCode) {
      this.step = "reset";
      this.resetForm.patchValue({ resetCode: data.resetCode });
    }
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.clearCountdown();
    // Release the object URL to free browser memory.
    if (this.captchaObjectUrl) {
      URL.revokeObjectURL(this.captchaObjectUrl);
    }
  }

  /**
   * Fetches a fresh captcha image from `GET /dashboard/captcha?type=reset&login=XXX`
   * and starts the 30-second countdown on success.
   * Does nothing if the modal is locked or the login field is empty.
   *
   * @param preserveError When true, any existing error message is kept visible during the
   *   reload (e.g. after a wrong-code submission so the user knows why a new captcha appeared).
   */
  public loadCaptcha(preserveError = false): void {
    if (this.captchaBlocked) return;

    const login = this.captchaForm.get("login").value?.trim();
    if (!login) {
      this.captchaForm.get("login").markAsTouched();
      return;
    }

    this.clearCountdown();
    this.captchaLoading = true;
    this.captchaLoaded = false;
    if (!preserveError) {
      this.captchaError = null;
    }

    // Revoke the previous object URL before creating a new one to avoid memory leaks.
    if (this.captchaObjectUrl) {
      URL.revokeObjectURL(this.captchaObjectUrl);
      this.captchaObjectUrl = null;
    }

    this.authApi.getCaptcha(login).subscribe(
      (blob) => {
        this.captchaObjectUrl = URL.createObjectURL(blob);
        this.captchaImageUrl = this.sanitizer.bypassSecurityTrustUrl(this.captchaObjectUrl);
        this.captchaLoading = false;
        this.captchaLoaded = true;
        this.startCountdown();
      },
      (err) => {
        this.captchaLoading = false;
        // Translate the server error key; fall back to a generic message if unmapped.
        this.captchaError = this.translateService.instant("api.errors." + (err?.message ?? "error"));
      }
    );
  }

  /**
   * Starts the 30-second countdown timer.
   * On expiry, auto-refreshes the captcha if the limit has not been reached,
   * otherwise locks the modal.
   */
  private startCountdown(): void {
    this.countdown = CAPTCHA_DURATION_S;
    this.captchaIntervalId = window.setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.clearCountdown();
        if (this.captchaAutoRefreshCount < MAX_CAPTCHA_AUTO_REFRESH) {
          this.captchaAutoRefreshCount++;
          this.loadCaptcha();
        } else {
          this.captchaBlocked = true;
          this.captchaError = this.translateService.instant("page.login.captchaMaxAttempts");
        }
      }
    }, 1000);
  }

  /**
   * Stops the running countdown and resets its handle to null.
   * Called before every new countdown and in `ngOnDestroy`.
   */
  private clearCountdown(): void {
    if (this.captchaIntervalId) {
      clearInterval(this.captchaIntervalId);
      this.captchaIntervalId = null;
    }
  }

  /**
   * Handles the main button click for the captcha step.
   *
   * - If the captcha image has not been loaded yet, triggers `loadCaptcha()`.
   * - Otherwise validates the form and calls `POST /dashboard/captcha`.
   *   - Success: shows the e-mail confirmation message.
   *   - Error: reloads the captcha and increments the attempt counter.
   *     A wrong code counts as an attempt, consistent with the timer-based refresh limit.
   */
  public submitCaptcha(): void {
    if (!this.captchaLoaded) {
      this.loadCaptcha();
      return;
    }
    if (!this.captchaForm.valid) {
      this.validateAllFormFields(this.captchaForm);
      return;
    }
    if (this.captchaBlocked) return;

    this.captchaLoading = true;
    this.captchaError = null;

    const login = this.captchaForm.get("login").value?.trim();
    const code = this.captchaForm.get("captchaCode").value;

    this.authApi.submitCaptcha("reset", login, code).subscribe(
      () => {
        this.clearCountdown();
        this.captchaLoading = false;
        this.captchaSent = true;
      },
      () => {
        this.captchaLoading = false;
        // Use a captcha-specific message regardless of the server error code,
        // because generic codes like AUTHENTICATION_FAILED are wrong here.
        this.captchaError = this.translateService.instant("page.login.forgotPassword.wrongCaptcha");
        this.captchaForm.get("captchaCode").reset();
        if (!this.captchaBlocked) {
          this.captchaAutoRefreshCount++;
          if (this.captchaAutoRefreshCount >= MAX_CAPTCHA_AUTO_REFRESH) {
            this.clearCountdown();
            this.captchaBlocked = true;
            this.captchaError = this.translateService.instant("page.login.captchaMaxAttempts");
          } else {
            this.loadCaptcha(true); // preserve the wrong-code error while the new captcha loads
          }
        }
      }
    );
  }

  /**
   * Manually refreshes the captcha on user request.
   * Counts as one attempt toward the auto-refresh limit (MAX_CAPTCHA_AUTO_REFRESH).
   */
  public refreshCaptchaManually(): void {
    if (this.captchaBlocked) return;
    this.captchaAutoRefreshCount++;
    if (this.captchaAutoRefreshCount >= MAX_CAPTCHA_AUTO_REFRESH) {
      this.clearCountdown();
      this.captchaBlocked = true;
      this.captchaError = this.translateService.instant("page.login.captchaMaxAttempts");
      return;
    }
    this.loadCaptcha();
  }

  /**
   * Advances to step 2 (password reset form).
   * Pre-fills the login field if it was already entered in step 1.
   */
  public goToResetStep(): void {
    const login = this.captchaForm.get("login").value?.trim();
    if (login) {
      this.resetForm.patchValue({ login });
    }
    this.step = "reset";
  }

  /**
   * Submits the new password via `POST /dashboard/newPractitionerPassword`.
   *
   * On success:
   * - Stores the access token in the session.
   * - Loads the user account (`UserService.account()`) to populate roles and preferences.
   * - Closes the modal with `{ success: true }` so the login page handles navigation.
   *
   * On error (expired code, invalid code, etc.), displays the server error message.
   */
  public submitResetPassword(): void {
    if (!this.resetForm.valid) {
      this.validateAllFormFields(this.resetForm);
      return;
    }

    this.resetLoading = true;
    this.resetError = null;

    const login = this.resetForm.get("login").value?.trim();
    const resetCode = this.resetForm.get("resetCode").value?.trim();
    const password = this.resetForm.get("newPasswordA").value;

    this.authApi.setNewPractitionerPassword(password, resetCode, login).subscribe(
      (result) => {
        this.sessionService.token = result.token;
        // Load the account before closing so the session is fully populated (roles, preferences…).
        this.userService
          .account()
          .pipe(first())
          .subscribe(
            () => {
              this.resetLoading = false;
              this.dialogRef.close({ success: true });
            },
            () => {
              // Non-blocking failure: close anyway and let the navigation layer recover.
              this.resetLoading = false;
              this.dialogRef.close({ success: true });
            }
          );
      },
      (err) => {
        this.resetLoading = false;
        this.resetError = err?.message ?? "error";
      }
    );
  }

  /** Closes the modal without taking any action. */
  public close(): void {
    this.dialogRef.close();
  }

  /**
   * Returns the active form depending on the current step.
   * Used by `FormPage` validation helpers (`isFieldValid`, etc.).
   */
  public getForm(): UntypedFormGroup {
    return this.step === "captcha" ? this.captchaForm : this.resetForm;
  }
}
